import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import SmarttextContentTable from 'components/shared/Smartcontenttable';
import SmartContentPopin from 'components/shared/SmartcontentModal/SmartContentPopin';
import { ORDERSTATUS } from 'utils/Constants';
import axiosPrivate from '../../../config/axiosPrivate';
import { useWebsiteFilter } from 'utils/hooks/useWebsiteFilter';
import { useDispatch, useSelector } from 'react-redux';
import { adminClientClientListSelector } from 'store/AdminClient/ClientList';
import {
  aiSmartContentTypeSelector,
  setAISmartContentType,
} from 'store/AdminClient/AISmartContentType';
import { fetchAdminClientClientList } from 'store/AdminClient/ClientList/features';
import ConfirmationModal from 'components/shared/modal/ConfirmationModal';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';
import { ExcelExportButton } from 'components/shared/ExcelExport/ExcelExportButton';
import { useTextStatusChange, isTextRevisionNotAllowedError } from 'utils/hooks/useTextStatusChange';
import QuitProjectCreationModal from 'components/shared/modal/QuitProjectCreationModal';
import AssignTextToProjectPopin from 'components/shared/SmartcontentModal/AssignTextToProjectPopin';
import TextAssignedToProjectModal from 'components/shared/modal/TextAssignedToProjectModal';
import { useAssignTextToProject } from 'utils/smartContent/useAssignTextToProject';
import './AISmartcontent.css';
import usePageTracking from 'utils/hooks/usePageTracking';
import { formatWebsiteThematicsService } from 'utils/websiteThematics';
import { consultantWebsiteListSelector } from 'store/Consultant/WebsiteList';
import { fetchConsultantWebsiteList } from 'store/Consultant/WebsiteList/features';
import { CONSULTANT_PROJECT_ROUTES } from 'pages/consultant/projects/constants';

const SMART_POPIN_CONTEXT_KEY = 'SmartContentPopinContext';

const CONTENT_CONFIG = {
  PRODUCT: {
    title: 'Fiches produit SEO',
    apiContentType: 'PRODUCT',
    agentType: 'PRODUCT',
    createPath: '/AdminClient/createIAsmarttext',
    excelTextDocumentType: 'PRODUCT',
  },
  LONG_CATEGORY: {
    title: 'Pages catégories',
    apiContentType: 'LONG_CATEGORY',
    agentType: 'LONG_CATEGORY',
    createPath: '/AdminClient/createIAsmarttext',
    excelTextDocumentType: 'LONG_CATEGORY',
  },
  GMB: {
    title: 'Fiches GMB',
    apiContentType: 'GMB',
    agentType: 'GMB',
    createPath: '/AdminClient/createIAsmarttext',
    excelTextDocumentType: 'GMB',
  },
  STORE_LOCATOR: {
    title: 'Fiches store locator',
    apiContentType: 'STORE_LOCATOR',
    agentType: 'STORE_LOCATOR',
    createPath: '/AdminClient/createIAsmarttext',
    excelTextDocumentType: 'STORE_LOCATOR',
  },
  CONTENT_IMPROVEMENT: {
    title: 'Enrichissement SEO',
    apiContentType: 'CONTENT_IMPROVEMENT',
    agentType: 'CONTENT_IMPROVEMENT',
    createPath: '/AdminClient/createIAsmarttext',
    excelTextDocumentType: 'PRODUCT',
  },
  OTHER: {
    title: 'Contenu web',
    apiContentType: 'OTHER',
    agentType: 'OTHER',
    createPath: '/AdminClient/createIAsmarttext',
    excelTextDocumentType: 'OTHER',
  },
  YOUTUBE: {
    title: 'Youtube',
    apiContentType: 'YOUTUBE',
    agentType: 'YOUTUBE',
    createPath: '/AdminClient/createIAsmarttext',
    excelTextDocumentType: 'YOUTUBE',
  },
};

const getContentConfig = (key) => CONTENT_CONFIG[key] || CONTENT_CONFIG.PRODUCT;
const ENRICHMENT_LLM_OPTIONS = [
  { id: 'claude', name: 'Claude' },
  { id: 'gemini', name: 'Gemini' },
];
const normalizeLlmId = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'both' || normalized === 'claude+gpt' || normalized === 'gpt+claude') {
    return 'claude_gpt';
  }
  return normalized.replace(/\s+/g, '_');
};

const formatModelName = (id) => {
  if (id === 'gpt') return 'GPT';
  if (id === 'claude') return 'Claude';
  if (id === 'claude_gpt') return 'Claude + GPT';
  if (id === 'perplexity') return 'Perplexity';
  if (id === 'gemini') return 'Gemini';
  if (id === 'premium') return 'premium';
  return String(id || '').toUpperCase();
};

const formatQuotaDisplay = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) {
    return '—';
  }
  return Number(value).toLocaleString('fr-FR');
};

function AISmartcontent({ presetType, consultantMode = false }) {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();

  const [texts, setTexts] = useState([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [websiteId, setWebsiteId] = useState(null);
  const [isSmartContentPopinOpen, setIsSmartContentPopinOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [allTexts, setAllTexts] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [gapAnalysisEnabled, setGapAnalysisEnabled] = useState(null);
  const [availableAiModels, setAvailableAiModels] = useState([]);
  const [remainingCredits, setRemainingCredits] = useState(null);
  const [remainingTexts, setRemainingTexts] = useState(null);
  const [isCreditsLimitModalOpen, setIsCreditsLimitModalOpen] = useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);
  const [isRevisionErrorModalOpen, setIsRevisionErrorModalOpen] = useState(false);

  const [selectedWebsiteUrl] = useWebsiteFilter();
  const { clients, isLoadingClients = false } = useSelector(adminClientClientListSelector) || {};
  const {
    websites: consultantWebsites,
    isLoadingWebsites: isLoadingConsultantWebsites = false,
  } = useSelector(consultantWebsiteListSelector) || {};
  const clientsList = consultantMode
    ? (Array.isArray(consultantWebsites) ? consultantWebsites : [])
    : (Array.isArray(clients) ? clients : []);
  const websitesLoaded = consultantMode
    ? consultantWebsites !== null
    : clients !== null;
  const isLoadingWebsitesList = consultantMode ? isLoadingConsultantWebsites : isLoadingClients;
  const hasNoWebsiteConfigured =
    websitesLoaded && !isLoadingWebsitesList && clientsList.length === 0;
  const currentType = useSelector(aiSmartContentTypeSelector);

  const effectiveType = presetType || currentType || 'PRODUCT';
  usePageTracking(
    consultantMode
      ? `/Consultant/IA_Smart/${effectiveType}`
      : `/AdminClient/IA_Smart/${effectiveType}`
  );

  const {
    title,
    apiContentType,
    agentType,
    createPath: adminCreatePath,
  } = getContentConfig(effectiveType);
  const createPath = consultantMode
    ? CONSULTANT_PROJECT_ROUTES.CREATE_IA_SMART_TEXT
    : adminCreatePath;

  useEffect(() => {
    if (presetType && presetType !== currentType) {
      dispatch(setAISmartContentType(presetType));
    }
  }, [presetType, currentType, dispatch]);

  useEffect(() => {
    document.title = 'Typix - Mes contenus IA';
  }, [effectiveType]);

  useEffect(() => {
    let isMounted = true;

    const fetchCompanyQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get('/project/company-quotas');
        if (!isMounted) return;
        setGapAnalysisEnabled(data?.gapAnalysisEnabled === true);
        const totalCredits = Number(data?.totalCredits);
        const usedCredits = Number(data?.usedCredits);
        if (Number.isFinite(totalCredits) && Number.isFinite(usedCredits)) {
          setRemainingCredits(totalCredits - usedCredits);
        } else {
          const apiRemainingCredits = Number(data?.remainingCredits);
          setRemainingCredits(Number.isFinite(apiRemainingCredits) ? apiRemainingCredits : null);
        }
        const rawModels = Array.isArray(data?.availableAiModels) ? data.availableAiModels : [];
        const normalizedModels = rawModels
          .map((model) => {
            const id = normalizeLlmId(
              typeof model === 'string' ? model : (model?.id || model?.value || model?.name || '')
            );
            if (!id) return null;
            return {
              id,
              name: typeof model === 'object' && model?.name ? model.name : formatModelName(id),
            };
          })
          .filter(Boolean);
        setAvailableAiModels(
          Array.from(new Map(normalizedModels.map((item) => [item.id, item])).values())
        );
      } catch (error) {
        if (isMounted) {
          setGapAnalysisEnabled(false);
          setAvailableAiModels([]);
          setRemainingCredits(null);
        }
      }
    };

    fetchCompanyQuotas();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!websiteId) {
      return undefined;
    }

    let isMounted = true;

    const fetchWebsiteQuotas = async () => {
      try {
        const { data } = await axiosPrivate.get(`/project/website-quotas/${websiteId}`);
        if (!isMounted) return;

        const maxTexts = Number(data?.maxTexts);
        const usedTexts = Number(data?.usedTexts);
        if (Number.isFinite(maxTexts) && Number.isFinite(usedTexts)) {
          setRemainingTexts(Math.max(0, maxTexts - usedTexts));
        } else {
          setRemainingTexts(null);
        }
      } catch {
        if (isMounted) {
          setRemainingTexts(null);
        }
      }
    };

    fetchWebsiteQuotas();
    return () => {
      isMounted = false;
    };
  }, [websiteId, consultantMode]);

  const normalizeUrl = (input) => {
    return (input || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '');
  };


  useEffect(() => {
    if (consultantMode) {
      if (consultantWebsites === null) {
        dispatch(fetchConsultantWebsiteList());
      }
      return;
    }

    if (clients === null) {
      try { console.log('[AISmartcontent] dispatch fetchAdminClientClientList'); } catch { }
      dispatch(fetchAdminClientClientList());
    } else {
      try { console.log('[AISmartcontent] clients present', { len: clientsList.length }); } catch { }
    }
  }, [dispatch, clients, consultantMode, consultantWebsites, clientsList.length]);

  useEffect(() => {
    const fetchUnassigned = async () => {
      try {
        console.log('[AISmartcontent] fetchUnassigned start', {
          selectedWebsiteUrl,
          clientsLen: clientsList.length,
          apiContentType,
          agentType,
        });
      } catch { }
      setTexts([]);
      setAllTexts([]);

      if (!websitesLoaded) {
        return;
      }

      if (!selectedWebsiteUrl) {
        setTexts([]);
        setAllTexts([]);
        setWebsiteId(null);
        setIsLoading(false);
        return;
      }

      if (clientsList.length === 0) {
        try { console.log('[AISmartcontent] early return: clients empty'); } catch { }
        setWebsiteId(null);
        setAgents([]);
        setIsLoading(false);
        return;
      }

      setIsError(false);
      setIsLoading(true);
      try {
        const rawList = clientsList;
        const normalizedSaved = normalizeUrl(selectedWebsiteUrl);
        const selected = rawList.find((w) => normalizeUrl(w?.urlSite || w?.url || w?.name) === normalizedSaved);
        const websiteId = selected?.id;
        try { console.log('[AISmartcontent] resolved websiteId', { websiteId, normalizedSaved }); } catch { }

        if (!websiteId) {
          setTexts([]);
          setAllTexts([]);
          setWebsiteId(null);
          setIsLoading(false);
          try { console.log('[AISmartcontent] no websiteId -> set empty and stop'); } catch { }
          return;
        }

        setWebsiteId(websiteId);
        try { console.log('[AISmartcontent] fetching texts for websiteId', websiteId, 'contentType', apiContentType); } catch { }
        const resp = await axiosPrivate.get(
          `/texts/external/unassigned/by-website/${websiteId}?type=NON_TRANSLATED&contentType=${apiContentType}`
        );
        const data = Array.isArray(resp?.data) ? resp.data : [];
        try { console.log('[AISmartcontent] texts fetched', { count: data.length }); } catch { }

        let agentsList = [];
        try {
          const websiteParam = encodeURIComponent(selectedWebsiteUrl);

          if (agentType === 'ALL') {
              const contentTypes = ['PRODUCT', 'LONG_CATEGORY', 'STORE_LOCATOR', 'GMB'];
              const requests = contentTypes.map((typeContenu) =>
                axiosPrivate.get(
                  `/smart-content/agent_ia/redaction/by_website_content_type?websiteUrl=${websiteParam}&typeContenu=${encodeURIComponent(typeContenu)}`
                )
              );

              requests.push(
                axiosPrivate.get(
                  `/smart-content/agent_ia/redaction/free_content_type?websiteUrl=${websiteParam}`
                )
              );

              const responses = await Promise.allSettled(requests);
              const merged = [];

              responses.forEach((res) => {
                if (res.status !== 'fulfilled') return;
                const items = Array.isArray(res.value?.data) ? res.value.data : [];
                merged.push(...items);
              });

              const uniqueMap = new Map();
              merged.forEach((agent) => {
                const key = String(agent?.id || agent?.agentId || '');
                if (!key) return;
                if (!uniqueMap.has(key)) uniqueMap.set(key, agent);
              });
              agentsList = Array.from(uniqueMap.values());
            } else {
              const agentsUrl =
                agentType === 'OTHER'
                  ? `/smart-content/agent_ia/redaction/free_content_type?websiteUrl=${websiteParam}`
                  : agentType === 'CONTENT_IMPROVEMENT'
                    ? `/smart-content/agent_ia/redaction/all?websiteUrl=${websiteParam}`
                    : `/smart-content/agent_ia/redaction/by_website_content_type?websiteUrl=${websiteParam}&typeContenu=${encodeURIComponent(agentType)}`;
              const agentsResp = await axiosPrivate.get(agentsUrl);
              agentsList = Array.isArray(agentsResp?.data) ? agentsResp.data : [];
            }

          setAgents(agentsList);
        } catch (agentError) {
          console.warn('Erreur lors de la récupération des agents:', agentError);
          agentsList = [];
          setAgents([]);
        }

        const mapped = data.map((item) => {
          let fontClass = 'text-gray'; // Brouillon par défaut
          let statusText = 'Brouillon';
          if (item.deployed === true) {
            fontClass = 'text-purple'; // En ligne
            statusText = 'En ligne';
          } else if (item.validated === true) {
            fontClass = 'text-green'; // Validé
            statusText = 'Validé';
          } else if (item.inRevision === true) {
            fontClass = 'text-orange';
            statusText = 'En révision';
          } else if (item.delivered === true) {
            fontClass = '';
            statusText = 'Livré';
          }

          let agentName = '-';
          if (item.aiAgent && item.aiAgent.name) {
            agentName = item.aiAgent.name;
          } else if (item.aiAgentId && item.aiAgentId.trim() !== '') {
            const agent = agentsList.find(
              (a) => a.id === item.aiAgentId || a.agentId === item.aiAgentId
            );
            agentName = agent
              ? agent.name || agent.agentName || `Agent ${item.aiAgentId}`
              : `Agent ${item.aiAgentId}`;
          }

          return {
            id: item.id || item.textId,
            title: item.title || item.keyword || '-',
            dateAdded: item.createdAt || item.dateCreation || null,
            dateUpdated:
              item.updatedAt ||
              item.lastUpdatedAt ||
              item.lastUpdated ||
              item.modifiedAt ||
              item.updateDate ||
              item.dateUpdated ||
              item.updated_date ||
              item.lastModificationDate ||
              null,
            url: item.url || item.urlPage || '-',
            status: statusText,
            agentName: agentName,
            aiAgent: item.aiAgent || null,
            type: item.type || 'SMART',
            font_class: fontClass,
            delivered: item.delivered || false,
            validated: item.validated || false,
            deployed: item.deployed || false,
            inRevision: item.inRevision || false,
            contentScore: item.contentScore || null,
            agentModel: item.aiAgent?.model || null,
          };
        });

        setAllTexts(mapped);
        setTexts(mapped);
        setIsLoading(false);
        try { console.log('[AISmartcontent] setTexts done', { mappedLen: mapped.length }); } catch { }
      } catch (e) {
        setIsError(true);
        setTexts([]);
        setAllTexts([]);
        setIsLoading(false);
        try { console.log('[AISmartcontent] error', e); } catch { }
      }
    };

    fetchUnassigned();
  }, [selectedWebsiteUrl, clientsList, websitesLoaded, apiContentType, agentType, consultantMode]);

  useEffect(() => {
    if (!searchValue || !searchValue.trim()) {
      setTexts(allTexts);
      return;
    }

    const q = searchValue.toLowerCase().trim();
    const filtered = allTexts.filter((row) => {
      const title = (row.title || '').toLowerCase();
      const url = (row.url || '').toLowerCase();
      const agentNameText = (row.agentName || '').toLowerCase();
      return (
        title.includes(q) ||
        url.includes(q) ||
        agentNameText.includes(q)
      );
    });

    setTexts(filtered);
  }, [searchValue, allTexts]);


  const fetchDisplayData = async (id) => {
    try {
      localStorage.setItem('AdminClient_TextId', id);
      history.push(`${createPath}/${id}`);
    } catch (error) {
      console.error('Erreur lors du chargement des détails du texte:', error);
      localStorage.setItem('AdminClient_TextId', id);
      history.push(`${createPath}/${id}`);
    }
  };

  const handleNewOptimization = () => {
    if (hasNoWebsiteConfigured) return;
    setIsSmartContentPopinOpen(true);
  };

  const handleGoToAddWebsite = () => {
    history.push('/AdminClient/AjouterSiteWeb');
  };

  const requestDelete = (row) => {
    setRowPendingDelete(row || null);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    setConfirmOpen(false);
    setRowPendingDelete(null);
  };

  const executeDelete = async () => {
    const id = rowPendingDelete?.id;
    if (!id) { closeConfirm(); return; }
    try {
      await axiosPrivate.delete(`/texts/external/unassigned/${id}/delete`);
      setTexts((prev) => Array.isArray(prev) ? prev.filter((r) => (r.id !== id)) : prev);
    } catch (e) {
      try { console.error('Erreur suppression texte', e); } catch { }
    } finally {
      closeConfirm();
    }
  };




  const { handleStatusChange } = useTextStatusChange(
    () => { },
    (error, previousItem) => {
      if (isTextRevisionNotAllowedError(error)) {
        setIsRevisionErrorModalOpen(true);
      }
      if (!previousItem) return;
      setTexts((prev) =>
        Array.isArray(prev)
          ? prev.map((text) => (text.id === previousItem.id ? previousItem : text))
          : prev
      );
    },
    (textId, nextValue) => {
      const statusFlags = {
        Brouillon: { status: 'Brouillon', delivered: false, validated: false, deployed: false, inRevision: false },
        Livré: { status: 'Livré', delivered: true, validated: false, deployed: false, inRevision: false },
        'En révision': { status: 'En révision', delivered: false, validated: false, deployed: false, inRevision: true },
        Validé: { status: 'Validé', delivered: true, validated: true, deployed: false, inRevision: false },
        'En ligne': { status: 'En ligne', delivered: true, validated: true, deployed: true, inRevision: false },
      };
      const flags = statusFlags[nextValue] || statusFlags.Brouillon;
      setTexts((prev) =>
        Array.isArray(prev)
          ? prev.map((text) =>
            text.id === textId
              ? {
                ...text,
                status: flags.status,
                delivered: flags.delivered,
                validated: flags.validated,
                deployed: flags.deployed,
                inRevision: flags.inRevision,
              }
              : text
          )
          : prev
      );
    }
  );

  const handleStatusUpdate = (row, nextValue) => {
    if (consultantMode && nextValue !== 'Brouillon') {
      return;
    }
    const previousItem = (Array.isArray(texts) ? texts : []).find((t) => t.id === row.id) || row;
    handleStatusChange(row.id, nextValue, previousItem);
  };

  const {
    isPopinOpen: isAssignProjectPopinOpen,
    isSuccessModalOpen: isTextAssignedModalOpen,
    textPendingAssign,
    assignedProject,
    projects: assignableProjects,
    loadingProjects: isLoadingAssignableProjects,
    isSaving: isAssigningToProject,
    assignError,
    openAssignPopin,
    closeAssignPopin,
    closeSuccessModal: closeTextAssignedModal,
    handleAssign,
  } = useAssignTextToProject({
    websiteId,
    onAssigned: (textId) => {
      setTexts((prev) => (Array.isArray(prev) ? prev.filter((row) => row.id !== textId) : prev));
      setAllTexts((prev) => (Array.isArray(prev) ? prev.filter((row) => row.id !== textId) : prev));
    },
  });

  const handleSmartContentPopinSubmit = (formValues) => {
    if (hasNoWebsiteConfigured) {
      setIsSmartContentPopinOpen(false);
      return;
    }
    if (remainingCredits !== null && remainingCredits <= 0) {
      setIsCreditsLimitModalOpen(true);
      return;
    }
    try {
      const normalizedSaved = normalizeUrl(selectedWebsiteUrl);
      const selectedWebsite = clientsList.find(
        (w) => normalizeUrl(w?.urlSite || w?.url || w?.name) === normalizedSaved
      );
      const resolvedWebsiteId = selectedWebsite?.id || websiteId || null;
      const websiteThematicsService = formatWebsiteThematicsService(selectedWebsite);

      const context = {
        type: effectiveType,
        form: {
          ...formValues,
          website: formValues.website || selectedWebsiteUrl,
          websiteThematicsService,
        },
        meta: {
          websiteId: resolvedWebsiteId,
          websiteUrl: selectedWebsiteUrl,
          websiteThematicsService,
          apiContentType,
          agentType,
        },
      };
      window.localStorage.setItem(SMART_POPIN_CONTEXT_KEY, JSON.stringify(context));
    } catch (e) {
      try {
        console.error('Failed to store SmartContent popin context', e);
      } catch { }
    }
    dispatch(setAISmartContentType(effectiveType));
    history.push(createPath);
    setIsSmartContentPopinOpen(false);
  };

  return (
    <div style={{ marginTop: '-5px' }}>
      <div className="box-info-header">
        <div className="b-child-1">
          <h1>{title ? t(title) : ''}</h1>
        </div>
        {!consultantMode && (
          <div className="b-child-2 d-flex justify-content-end align-items-center ai-smart-header-actions">
            <div className="ai-smart-stats-panel" aria-label={t('Quotas disponibles')}>
              <div className="ai-smart-stat-block">
                <div className="ai-smart-icon-wrapper ai-smart-icon-yellow" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                    <path d="M20 3v4" />
                    <path d="M22 5h-4" />
                    <path d="M4 17v2" />
                    <path d="M5 18H3" />
                  </svg>
                </div>
                <div className="ai-smart-stat-text">
                  <span className="ai-smart-stat-label">{t('Crédits IA')}</span>
                  <span className="ai-smart-stat-value">{formatQuotaDisplay(remainingCredits)}</span>
                </div>
              </div>
              <div className="ai-smart-stat-divider" aria-hidden="true" />
              <div className="ai-smart-stat-block">
                <div className="ai-smart-icon-wrapper ai-smart-icon-purple" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" x2="8" y1="13" y2="13" />
                    <line x1="16" x2="8" y1="17" y2="17" />
                    <line x1="10" x2="8" y1="9" y2="9" />
                  </svg>
                </div>
                <div className="ai-smart-stat-text">
                  <span className="ai-smart-stat-label">{t('Textes restants')}</span>
                  <span className="ai-smart-stat-value">{formatQuotaDisplay(remainingTexts)}</span>
                </div>
              </div>
            </div>
            <button
              className="ai-smart-btn-create"
              type="button"
              onClick={handleNewOptimization}
              disabled={hasNoWebsiteConfigured}
              aria-disabled={hasNoWebsiteConfigured}
            >
              + {t('Créer un nouveau contenu')}
            </button>
          </div>
        )}
      </div>
      <hr
        style={{
          border: 'none',
          borderBottom: '1px solid rgb(179, 180, 182)',
          marginLeft: '-30px',
          marginBottom: 8,
          width: 'calc(100% + 40px)',
        }}
      />

      {hasNoWebsiteConfigured && (
        <div className="ai-smart-help-banner" role="note" style={{ marginBottom: 12 }}>
          <div className="ai-smart-help-banner-left">
            <span className="ai-smart-help-banner-text">
              {t('Aucun contenu disponible — veuillez d’abord ajouter un siteweb.')}
            </span>
          </div>
          {!consultantMode && (
            <div className="ai-smart-help-banner-actions">
              <button
                type="button"
                onClick={handleGoToAddWebsite}
                className="ai-smart-help-banner-link"
              >
                {t('Ajouter un siteweb')}
              </button>
            </div>
          )}
        </div>
      )}

      {websiteId && (
        <div
          style={{
            marginBottom: 12,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 12,
          }}
        >
          {!isSearchOpen && (
            <button
              type="button"
              onClick={() => {
                try {
                  const btn = document.querySelector(
                    '.ai-smart-excel-wrapper .excel-export-button'
                  );
                  if (btn) btn.click();
                } catch (e) { }
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 14px',
                borderRadius: 8,
                border: '1px solid #E5E7EB',
                backgroundColor: '#FFFFFF',
                fontSize: 13,
                fontWeight: 500,
                color: '#111827',
                cursor: 'pointer',
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              <img
                src="/Images/icon-download-simple.png"
                alt="download texts"
                style={{ width: 14, height: 14 }}
              />
              <span>{t('Téléchargez tous les textes.')}</span>
            </button>
          )}
          {!isSearchOpen ? (
            <img
              src="/Images/search-icon.png"
              alt="search"
              onClick={() => setIsSearchOpen(true)}
              style={{
                width: '32px',
                height: '32px',
                cursor: 'pointer',
              }}
            />
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                maxWidth: '320px',
              }}
            >
              <div
                style={{
                  position: 'relative',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <img
                  src="/Images/search-icon2.png"
                  alt="search"
                  style={{
                    position: 'absolute',
                    left: '10px',
                    width: '16px',
                    height: '16px',
                    pointerEvents: 'none',
                  }}
                />
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder={t('Rechercher un texte...')}
                  style={{
                    width: '100%',
                    padding: '8px 36px 8px 34px',
                    border: '1px solid #E5E7EB',
                    borderRadius: '6px',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  setIsSearchOpen(false);
                  setSearchValue('');
                }}
                style={{
                  background: '#F9FAFB',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: '#111827',
                }}
              >
                {t('Fermer')}
              </button>
            </div>
          )}
        </div>
      )}

      <div
        className="ai-smart-excel-wrapper"
        style={{ display: 'none' }}
      >
        {websiteId && texts.length > 0 && (
          <ExcelExportButton textIds={texts.map((text) => text?.id).filter(Boolean)} />
        )}
      </div>

      <SmarttextContentTable
        orderStatus={ORDERSTATUS.SMART}
        texts={texts}
        isLoading={isLoading}
        isError={isError}
        fetchDisplayData={fetchDisplayData}
        onRowClickRedirectPath={createPath}
        onNewContentClick={consultantMode ? undefined : handleNewOptimization}
        onDelete={consultantMode ? undefined : requestDelete}
        onLinkToProject={
          consultantMode || hasNoWebsiteConfigured ? undefined : openAssignPopin
        }
        disableEmptyStateCreateButton={hasNoWebsiteConfigured}
        hideEmptyStateCreateButton={consultantMode}
        readOnlyMode={consultantMode}
        enableStatusDropdown={!consultantMode}
        onStatusChange={consultantMode ? undefined : handleStatusUpdate}
      />
          <ConfirmationModal
            open={confirmOpen}
            handleClose={closeConfirm}
            executeAction={executeDelete}
            message={t('Êtes-vous sûr de vouloir supprimer ce contenu ?')}
          />
          <SmartContentPopin
            isOpen={isSmartContentPopinOpen}
            onClose={() => setIsSmartContentPopinOpen(false)}
            agents={agents}
            agentsLoading={isLoading}
            contentType={effectiveType}
            llmOptions={effectiveType === 'CONTENT_IMPROVEMENT' ? ENRICHMENT_LLM_OPTIONS : availableAiModels}
            isTargetUrlRequired={effectiveType === 'CONTENT_IMPROVEMENT'}
            onBlockedGenerateClick={() => {
              setIsSmartContentPopinOpen(false);
              if (hasNoWebsiteConfigured) {
                if (!consultantMode) {
                  history.push('/AdminClient/AjouterSiteWeb');
                }
                return;
              }
              setIsCreditsLimitModalOpen(true);
            }}
            disableGenerateButton={
              hasNoWebsiteConfigured ||
              (remainingCredits !== null && remainingCredits <= 0)
            }
            gapAnalysisEnabled={gapAnalysisEnabled === true}
            showAdvancedSeoSection={effectiveType !== 'GMB' && effectiveType !== 'YOUTUBE'}
            showWritingAxis={effectiveType !== 'YOUTUBE'}
            showAgentContentTypeInDropdown={
              effectiveType === 'OTHER' || effectiveType === 'CONTENT_IMPROVEMENT'
            }
            language={
              clientsList.find(
                (w) =>
                  normalizeUrl(w?.urlSite || w?.url || w?.name) ===
                  normalizeUrl(selectedWebsiteUrl)
              )?.language || null
            }
            onSubmit={handleSmartContentPopinSubmit}
            mode="create"
          />
          <ProjectCreatedModal
            isOpen={isCreditsLimitModalOpen}
            onClose={() => setIsCreditsLimitModalOpen(false)}
            onConfirm={() => {
              setIsCreditsLimitModalOpen(false);
              setIsBuyCreditsModalOpen(true);
            }}
            title={t('Limite atteinte')}
            message={t(
              'Vous avez atteint la limite autorisée par votre forfait.\n\nRechargez votre compte dès maintenant pour continuer à générer du contenu, enrichir vos textes et utiliser vos agents sans interruption.'
            )}
            confirmButtonText={t('Acheter des crédits')}
            secondaryButtonText={t('Passer à une offre supérieure')}
            onSecondaryAction={() => {
              if (!consultantMode) {
                history.push('/AdminClient/Forfait');
              }
            }}
          />
          <BuyCreditsModal
            open={isBuyCreditsModalOpen}
            onClose={() => setIsBuyCreditsModalOpen(false)}
          />
          <QuitProjectCreationModal
            isOpen={isRevisionErrorModalOpen}
            onClose={() => setIsRevisionErrorModalOpen(false)}
            variant="enrichment"
            title={t('Mise en révision impossible')}
            message={t(
              'Ce texte est validé ou mis en ligne. Il ne peut pas être envoyé en révision.'
            )}
            primaryButtonLabel={t('Fermer')}
            onPrimaryAction={() => setIsRevisionErrorModalOpen(false)}
            showSecondaryButton={false}
            showSuggestion={false}
          />
          <AssignTextToProjectPopin
            isOpen={isAssignProjectPopinOpen}
            onClose={closeAssignPopin}
            projects={assignableProjects}
            loadingProjects={isLoadingAssignableProjects}
            isSaving={isAssigningToProject}
            errorKey={assignError}
            onSave={handleAssign}
            onCreateProject={
              consultantMode
                ? undefined
                : () => {
                    closeAssignPopin();
                    history.push('/AdminClient/CreateProject');
                  }
            }
            keyword={textPendingAssign?.title || ''}
          />
          <TextAssignedToProjectModal
            isOpen={isTextAssignedModalOpen}
            onClose={closeTextAssignedModal}
            onGoToProjects={() => {
              closeTextAssignedModal();
              history.push(
                consultantMode
                  ? CONSULTANT_PROJECT_ROUTES.IN_PROGRESS
                  : '/AdminClient/ProjectsEnCours'
              );
            }}
            project={assignedProject}
          />
    </div>
  );
}


export default AISmartcontent;
